import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { bonusService } from 'components/api/bonuses';
import { Bonus, BonusesParams, BonusesResponse } from 'types/bonus';

interface BonusesState {
  data: Bonus[];
  total: number;
  isLoading: boolean;
  error: string | null;
  params: BonusesParams;
}

const initialState: BonusesState = {
  data: [],
  total: 0,
  isLoading: false,
  error: null,
  params: {
    page: 1,
    limit: 10,
    sortField: 'id',
    sortValue: 'DESC',
  },
};

export const fetchBonuses = createAsyncThunk(
  'bonuses/fetchBonuses',
  async (params: BonusesParams, { rejectWithValue }) => {
    try {
      const response = await bonusService.getBonuses(params);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to fetch bonuses',
      );
    }
  },
);

export const createBonus = createAsyncThunk(
  'bonuses/createBonus',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await bonusService.createBonus(data);
      return response?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to create bonus',
      );
    }
  },
);

export const deleteBonus = createAsyncThunk(
  'bonuses/deleteBonus',
  async (id: string, { rejectWithValue }) => {
    try {
      await bonusService.deleteBonus(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to delete bonus',
      );
    }
  },
);

export const updateBonus = createAsyncThunk(
  'bonuses/updateBonus',
  async ({ id, data }: { id: string | number; data: any }, { rejectWithValue }) => {
    try {
      const response = await bonusService.updateBonus(id, data);
      return response?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to update bonus',
      );
    }
  },
);

export const updateBonusImage = createAsyncThunk(
  'bonuses/updateBonusImage',
  async (
    {
      id,
      image,
      siteId,
      bonusType,
    }: { id: string | number; image: File; siteId: string | number; bonusType: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await bonusService.updateBonusImage(image, siteId, bonusType);
      return response?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to update bonus image',
      );
    }
  },
);

export const toggleBonusStatus = createAsyncThunk(
  'bonuses/toggleBonusStatus',
  async ({ id, isEnabled }: { id: string | number; isEnabled: boolean }, { rejectWithValue }) => {
    try {
      const response = await bonusService.toggleBonusStatus(id, isEnabled);
      return { id, isEnabled, data: response?.data };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to toggle bonus status',
      );
    }
  },
);

const bonusesSlice = createSlice({
  name: 'bonuses',
  initialState,
  reducers: {
    setParams: (state, action: PayloadAction<Partial<BonusesParams>>) => {
      state.params = { ...state.params, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBonuses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBonuses.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.data = (action.payload.data?.definitions || []).map((def: any) => ({
          ...def,
          isEnabled: def.enabled,
          translations: def.translations || def.info?.translations || {},
          info: {
            ...def.info,
            title: def.title,
            imageUrl: def.imageUrl,
            description: def.description,
            translations: def.translations || def.info?.translations || {},
          },
        }));
        state.total = action.payload.data?.definitions?.length || 0;
      })
      .addCase(fetchBonuses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createBonus.pending, (state) => {
        state.error = null;
      })
      .addCase(createBonus.fulfilled, (state, action: PayloadAction<Bonus>) => {
        state.isLoading = false;
        if (action.payload) {
          state.data = [action.payload, ...state.data];
          state.total += 1;
        }
      })
      .addCase(createBonus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteBonus.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteBonus.fulfilled, (state, action: PayloadAction<string | number>) => {
        state.isLoading = false;
        state.data = state.data.filter((bonus) => String(bonus.id) !== String(action.payload));
        state.total -= 1;
      })
      .addCase(deleteBonus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateBonus.pending, (state) => {
        state.error = null;
      })
      .addCase(updateBonus.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        if (action.payload) {
          const index = state.data.findIndex(
            (bonus) => bonus.bonusType === action.payload.bonusType,
          );
          if (index !== -1) {
            state.data[index] = {
              ...state.data[index],
              ...action.payload,
              isEnabled: action.payload.enabled ?? state.data[index].isEnabled,
              translations:
                action.payload.translations ||
                action.payload.info?.translations ||
                state.data[index].translations,
              info: {
                ...state.data[index].info,
                title: action.payload.title || state.data[index].info?.title,
                imageUrl: action.payload.imageUrl || state.data[index].info?.imageUrl,
                description: action.payload.description || state.data[index].info?.description,
                translations:
                  action.payload.translations ||
                  action.payload.info?.translations ||
                  state.data[index].info?.translations,
              },
            };
          }
        }
      })
      .addCase(updateBonus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateBonusImage.pending, (state) => {
        state.error = null;
      })
      .addCase(updateBonusImage.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        if (action.payload) {
          const index = state.data.findIndex(
            (bonus) => bonus.bonusType === action.payload.bonusType,
          );
          if (index !== -1) {
            state.data[index] = {
              ...state.data[index],
              ...action.payload,
              isEnabled: action.payload.enabled,
              info: {
                ...state.data[index].info,
                title: action.payload.title,
                imageUrl: action.payload.imageUrl,
              },
            };
          }
        }
      })
      .addCase(updateBonusImage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(toggleBonusStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(toggleBonusStatus.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        if (action.payload && action.payload.data) {
          const updatedDef = action.payload.data;
          const index = state.data.findIndex((bonus) => bonus.id === updatedDef.id);
          if (index !== -1) {
            state.data[index] = {
              ...state.data[index],
              ...updatedDef,
              isEnabled: updatedDef.enabled,
            };
          }
        } else if (action.payload) {
          const { id, isEnabled } = action.payload;
          const index = state.data.findIndex((bonus) => bonus.id === id);
          if (index !== -1) {
            state.data[index].isEnabled = isEnabled;
          }
        }
      })
      .addCase(toggleBonusStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setParams } = bonusesSlice.actions;
export default bonusesSlice.reducer;
